import { inject, Injectable, signal } from "@angular/core";
import { registerLocaleData } from "@angular/common";
import localeRo from "@angular/common/locales/ro";
import { NzI18nService } from "ng-zorro-antd/i18n";

import {
  ANGULAR_LOCALES,
  DEFAULT_LANGUAGE,
  Language,
  NZ_LOCALES,
  SUPPORTED_LANGUAGES
} from "./language";
import { en } from "./translations/en";
import { ro } from "./translations/ro";

const TRANSLATIONS: Record<Language, Record<string, string>> = { en, ro };

const LANG_STORAGE_KEY = "sb_lang";

registerLocaleData(localeRo);

@Injectable({ providedIn: "root" })
export class I18nService {
  private readonly nzI18n = inject(NzI18nService);

  private readonly _language = signal<Language>(this.resolveInitialLanguage());

  readonly language = this._language.asReadonly();

  constructor() {
    this.applyNzLocale(this._language());
  }

  setLanguage(lang: Language): void {
    this._language.set(lang);
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    this.applyNzLocale(lang);
  }

  /** The Angular locale id (for DatePipe, etc.) matching the current app language. */
  dateLocale(): string {
    return ANGULAR_LOCALES[this._language()];
  }

  translate(key: string, params?: Record<string, string>): string {
    const value = TRANSLATIONS[this._language()][key] ?? key;

    if (!params) return value;

    return Object.entries(params).reduce(
      (result, [k, v]) => result.replace(new RegExp(`\\{\\{${k}\\}\\}`, "g"), v),
      value
    );
  }

  private resolveInitialLanguage(): Language {
    const stored = localStorage.getItem(LANG_STORAGE_KEY) as Language | null;
    return stored && SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
  }

  private applyNzLocale(lang: Language): void {
    this.nzI18n.setLocale(NZ_LOCALES[lang]);
  }
}
